import type { NullableGrantEntry } from "../../shared/rbac/roles";
import { useZero } from "@rocicorp/zero/react";
import { sharedMutators } from "../../shared/mutators";
import type { ShareableScopeKind } from "../../shared/rbac/permissions";
import {
  type GrantEntry,
  grantHasPermission,
  rolesForScope,
  SCOPE_MANAGE_GRANTS_PERMISSION,
} from "../../shared/rbac/roles";
import { customRolesForScope, grantForMutation } from "../lib/grantAssembly";
import { deriveScopeGrantDelta } from "../lib/spaceGrantDelta";
import { useHasAccess } from "./useHasAccess";
import { useManageAccessGuard } from "./useManageAccessGuard";

type RawGrant = Partial<NullableGrantEntry>;

type CustomRole = {
  id: string;
  name: string;
  scopeKind: string;
  rolePermissions: ReadonlyArray<{ permission: string }>;
};

type PersistedParams<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = BaseParams<G, U> & {
  scopeId: string;
  ready: boolean;
  rawGrants: readonly RawGrant[] | undefined;
  viewerId: string | undefined;
  resourceLabel: string;
  guardCommit?: (
    grants: GrantEntry[],
    proceed: (grants: GrantEntry[]) => void
  ) => void;
};

type LocalParams<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = BaseParams<G, U> & {
  grants: GrantEntry[];
  onCommit: (grants: GrantEntry[]) => void;
};

type BaseParams<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = {
  scopeKind: ShareableScopeKind;
  allGroups: readonly G[];
  allUsers: readonly U[];
  customRoles: readonly CustomRole[];
};

type Params<
  G extends { id: string },
  U extends { id: string; deactivated?: boolean | null },
> = PersistedParams<G, U> | LocalParams<G, U>;

export function useScopeGrants<
  G extends {
    id: string;
    roleGrantsAsScope?: readonly { userId?: string | null }[];
  },
  U extends { id: string; deactivated?: boolean | null },
>(params: Params<G, U>) {
  const z = useZero();
  const { scopeKind, allGroups, allUsers, customRoles } = params;
  const managePermission = SCOPE_MANAGE_GRANTS_PERMISSION[scopeKind];

  const persisted = "scopeId" in params;
  const scopeId = persisted ? params.scopeId : undefined;
  const rawGrants = persisted ? params.rawGrants : undefined;
  const viewerId = persisted ? params.viewerId : undefined;
  const resourceLabel = persisted ? params.resourceLabel : "";
  const guardCommit = persisted ? params.guardCommit : undefined;
  const localGrants = persisted ? undefined : params.grants;
  const onCommit = persisted ? undefined : params.onCommit;

  const scopeCustomRoles = customRolesForScope(customRoles, scopeKind);

  const roleOptions = rolesForScope(scopeKind, scopeCustomRoles);

  const allGrants: GrantEntry[] = (() => {
    if (localGrants) return localGrants;
    if (!rawGrants) return [];
    return rawGrants
      .filter((g) => g.userGroupId || g.userId || g.serviceAccountId)
      .map((g) => ({
        userGroupId: g.userGroupId ?? undefined,
        userId: g.userId ?? undefined,
        serviceAccountId: g.serviceAccountId ?? undefined,
        builtinRoleId: g.builtinRoleId ?? undefined,
        customRoleId: g.customRoleId ?? undefined,
      }));
  })();

  const assignedGroupIds = new Set(
    allGrants.map((g) => g.userGroupId).filter((id): id is string => id != null)
  );

  const assignedUserIds = new Set(
    allGrants.map((g) => g.userId).filter((id): id is string => id != null)
  );

  const assignedGroups = allGroups.filter((g) => assignedGroupIds.has(g.id));

  const assignedUsers = allUsers.filter(
    (u) => assignedUserIds.has(u.id) && !u.deactivated
  );

  const customRoleMap = new Map(scopeCustomRoles.map((r) => [r.id, r]));

  const isManagerGrant = (g: GrantEntry) =>
    grantHasPermission(
      {
        builtinRoleId: g.builtinRoleId ?? null,
        customRole: g.customRoleId
          ? (customRoleMap.get(g.customRoleId) ?? null)
          : null,
      },
      managePermission
    );

  const viewerGroupIds = (() => {
    const ids = new Set<string>();
    if (!viewerId) return ids;
    for (const group of allGroups) {
      if (group.roleGrantsAsScope?.some((r) => r.userId === viewerId))
        ids.add(group.id);
    }
    return ids;
  })();

  const viewerIsTenantAdmin = useHasAccess("allow_all");

  const viewerRetainsManage = (grants: GrantEntry[]) => {
    if (!viewerId || viewerIsTenantAdmin) return true;
    return grants.some(
      (g) =>
        isManagerGrant(g) &&
        ((g.userId != null && g.userId === viewerId) ||
          (g.userGroupId != null && viewerGroupIds.has(g.userGroupId)))
    );
  };

  const doCommit = (nextGrants: GrantEntry[]) => {
    if (onCommit) {
      onCommit(nextGrants);
      return;
    }
    const proceed = (grants: GrantEntry[]) => {
      // Every scope kind commits through patchScopeGrants: one atomic
      // add/remove delta rather than a full-list replace. autoGrant applies
      // only to spaces (the sole cross-resource dependent).
      const { add, remove } = deriveScopeGrantDelta(allGrants, grants);
      z.mutate(
        sharedMutators.patchScopeGrants({
          scopeId: scopeId!,
          scopeKind,
          add: add.map(grantForMutation),
          remove,
          autoGrantConnectors: scopeKind === "space",
          autoGrantSkills: scopeKind === "space",
        })
      );
    };
    if (guardCommit) guardCommit(nextGrants, proceed);
    else proceed(nextGrants);
  };

  const { commitGrants, confirmModal: managerConfirmModal } =
    useManageAccessGuard({
      allGrants,
      doCommit,
      isManagerGrant,
      viewerRetainsManage,
      resourceLabel,
      enabled: persisted,
      ready: !persisted || params.ready,
    });

  const removeGroup = (groupId: string) => {
    commitGrants(allGrants.filter((g) => g.userGroupId !== groupId));
  };

  const removeUser = (userId: string) => {
    commitGrants(allGrants.filter((g) => g.userId !== userId));
  };

  return {
    allGrants,
    assignedGroups,
    assignedUsers,
    roleOptions,
    commitGrants,
    removeGroup,
    removeUser,
    managerConfirmModal,
  };
}
